import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useLocalPref } from '../../hooks/useLocalPref';

export interface ProgressTask {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  completedAt?: Date;
  category: 'account' | 'transactions' | 'budget' | 'organization';
  priority: 'high' | 'medium' | 'low';
}

export interface ProgressChecklistState {
  tasks: ProgressTask[];
  hiddenTasks: string[];
  completedCount: number;
  totalCount: number;
  isCompleted: boolean;
  showChecklist: boolean;
  isModalOpen: boolean;
  lastCompletedTask?: ProgressTask;
}

export interface ProgressChecklistContextType {
  state: ProgressChecklistState;
  markTaskCompleted: (taskId: string) => void;
  markTaskIncomplete: (taskId: string) => void;
  toggleChecklist: () => void;
  hideChecklist: () => void;
  showChecklist: () => void;
  dismissChecklist: (durationMs: number) => void;
  restoreChecklist: () => void;
  resetProgress: () => void;
  openModal: () => void;
  closeModal: () => void;
  hideTask: (taskId: string) => void;
}

const defaultTasks: ProgressTask[] = [
  {
    id: 'link-account',
    title: 'Link an Account',
    description: 'Connect your bank account to start tracking transactions automatically',
    completed: false,
    category: 'account',
    priority: 'high',
  },
  {
    id: 'categorize-5-transactions',
    title: 'Categorize 5 Transactions',
    description: 'Organize your transactions by assigning categories to at least 5 transactions',
    completed: false,
    category: 'transactions',
    priority: 'high',
  },
  {
    id: 'review-5-transactions',
    title: 'Review 5 Transactions',
    description: 'Review and update notes/categories for 5 transactions, especially auto-categorized ones',
    completed: false,
    category: 'transactions',
    priority: 'high',
  },
  {
    id: 'create-rule',
    title: 'Create a Rule',
    description: 'Set up an automatic categorization rule to save time on future transactions',
    completed: false,
    category: 'transactions',
    priority: 'medium',
  },
  {
    id: 'batch-action',
    title: 'Use Batch Action',
    description: 'Perform a batch action from the Account transactions ledger',
    completed: false,
    category: 'transactions',
    priority: 'medium',
  },
  {
    id: 'clear-uncategorized',
    title: 'Clear Uncategorized Transactions',
    description: 'Review and categorize all uncategorized transactions',
    completed: false,
    category: 'transactions',
    priority: 'medium',
  },
  {
    id: 'clear-unreviewed',
    title: 'Clear Unreviewed Transactions',
    description: 'Review all transactions marked as unreviewed',
    completed: false,
    category: 'transactions',
    priority: 'medium',
  },
  {
    id: 'create-budget',
    title: 'Create a Budget',
    description: 'Set up your first budget using "Set average for a period" or "Populate using budget template"',
    completed: false,
    category: 'budget',
    priority: 'high',
  },
  {
    id: 'rename-budget',
    title: 'Rename Budget File',
    description: 'Personalize your budget by giving it a custom name',
    completed: false,
    category: 'organization',
    priority: 'low',
  },
  {
    id: 'export-backup',
    title: 'Export a Backup',
    description: 'Create a backup of your data for safety and peace of mind',
    completed: false,
    category: 'organization',
    priority: 'low',
  },
];

const ProgressChecklistContext = createContext<ProgressChecklistContextType | undefined>(undefined);

export function ProgressChecklistProvider({ children }: { children: ReactNode }) {
  // Load completed tasks from preferences
  const [completedTasksPref, setCompletedTasksPref] = useLocalPref('progressChecklist.completedTasks');
  const [showChecklistPref, setShowChecklistPref] = useLocalPref('progressChecklist.showChecklist');
  const [dismissedUntilPref, setDismissedUntilPref] = useLocalPref('progressChecklist.dismissedUntil');
  const [dismissCountPref, setDismissCountPref] = useLocalPref('progressChecklist.dismissCount');
  
  // Initialize tasks with completed status from preferences
  const initializeTasks = useCallback((completedTaskIds: string[] = []) => {
    return defaultTasks.map(task => {
      const isCompleted = completedTaskIds.includes(task.id);
      return {
        ...task,
        completed: isCompleted,
        completedAt: isCompleted ? new Date() : undefined,
      };
    });
  }, []);

  // Use local state as the primary state management
  const [progressData, setProgressData] = useState({
    tasks: defaultTasks,
    hiddenTasks: [] as string[],
    showChecklist: true,
    isModalOpen: false,
  });

  // Tracks current time so the snooze expiry re-renders automatically
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!dismissedUntilPref || dismissedUntilPref <= Date.now()) return;
    const delay = dismissedUntilPref - Date.now();
    const id = setTimeout(() => setNow(Date.now()), delay);
    return () => clearTimeout(id);
  }, [dismissedUntilPref]);

  // Load from preferences on mount and when preferences change
  useEffect(() => {
    const completedTaskIds = completedTasksPref || [];
    const tasks = initializeTasks(completedTaskIds);
    setProgressData(prevData => ({
      ...prevData,
      tasks,
      showChecklist: showChecklistPref !== undefined ? showChecklistPref : prevData.showChecklist,
    }));
  }, [completedTasksPref, showChecklistPref, initializeTasks]);

  // Memoize the state calculation to prevent unnecessary re-renders
  const state = useMemo<ProgressChecklistState>(() => {
    const tasks = progressData.tasks || defaultTasks;
    const completedCount = tasks.filter(task => task.completed).length;
    const totalCount = tasks.length;
    const isPermanentlyHidden = progressData.showChecklist === false;
    const isSnoozed = !!dismissedUntilPref && now < dismissedUntilPref;

    return {
      tasks,
      hiddenTasks: progressData.hiddenTasks || [],
      completedCount,
      totalCount,
      isCompleted: completedCount === totalCount,
      showChecklist: !isPermanentlyHidden && !isSnoozed,
      isModalOpen: progressData.isModalOpen ?? false,
    };
  }, [progressData, dismissedUntilPref, now]);

  const markTaskCompleted = useCallback((taskId: string) => {
    // First, get the current completed tasks from preferences and add the new one
    const currentCompletedTasks = completedTasksPref || [];
    const newCompletedTaskIds = [...new Set([...currentCompletedTasks, taskId])];
    
    // Save to preferences first
    setCompletedTasksPref(newCompletedTaskIds);
    
    // Then update local state
    setProgressData(prevData => {
      const updatedTasks = (prevData?.tasks || defaultTasks).map(task =>
        task.id === taskId
          ? { ...task, completed: true, completedAt: new Date() }
          : task
      );
      
      return {
        ...prevData,
        tasks: updatedTasks,
      };
    });
  }, [setCompletedTasksPref, completedTasksPref]);

  const markTaskIncomplete = useCallback((taskId: string) => {
    // First, get the current completed tasks from preferences and remove the task
    const currentCompletedTasks = completedTasksPref || [];
    const newCompletedTaskIds = currentCompletedTasks.filter((id: string) => id !== taskId);
    
    // Save to preferences first
    setCompletedTasksPref(newCompletedTaskIds);
    
    // Then update local state
    setProgressData(prevData => {
      const updatedTasks = (prevData?.tasks || defaultTasks).map(task =>
        task.id === taskId
          ? { ...task, completed: false, completedAt: undefined }
          : task
      );
      
      return {
        ...prevData,
        tasks: updatedTasks,
      };
    });
  }, [setCompletedTasksPref, completedTasksPref]);

  const toggleChecklist = useCallback(() => {
    setProgressData(prevData => ({
      ...prevData,
      showChecklist: !(prevData?.showChecklist ?? true),
    }));
  }, []);

  const hideChecklist = useCallback(() => {
    setShowChecklistPref(false);
    setProgressData(prevData => ({
      ...prevData,
      showChecklist: false,
    }));
  }, [setShowChecklistPref]);

  const showChecklist = useCallback(() => {
    setShowChecklistPref(true);
    setProgressData(prevData => ({
      ...prevData,
      showChecklist: true,
    }));
  }, [setShowChecklistPref]);

  const dismissChecklist = useCallback((durationMs: number) => {
    setDismissedUntilPref(Date.now() + durationMs);
    setDismissCountPref((dismissCountPref || 0) + 1);
  }, [setDismissedUntilPref, setDismissCountPref, dismissCountPref]);

  const restoreChecklist = useCallback(() => {
    setShowChecklistPref(true);
    setDismissedUntilPref(undefined);
    setProgressData(prevData => ({
      ...prevData,
      showChecklist: true,
    }));
  }, [setShowChecklistPref, setDismissedUntilPref]);

  const resetProgress = useCallback(() => {
    setCompletedTasksPref([]);
    setShowChecklistPref(true);
    setDismissedUntilPref(undefined);
    setDismissCountPref(undefined);
    setProgressData({
      tasks: defaultTasks,
      hiddenTasks: [],
      showChecklist: true,
      isModalOpen: false,
    });
  }, [setCompletedTasksPref, setShowChecklistPref, setDismissedUntilPref, setDismissCountPref]);

  const hideTask = useCallback((taskId: string) => {
    setProgressData(prevData => ({
      ...prevData,
      hiddenTasks: [...(prevData.hiddenTasks || []), taskId],
    }));
  }, []);

  const openModal = useCallback(() => {
    console.log('ProgressChecklistContext: Opening modal...');
    setProgressData(prevData => ({
      ...prevData,
      isModalOpen: true,
    }));
  }, []);

  const closeModal = useCallback(() => {
    console.log('ProgressChecklistContext: Closing modal...');
    setProgressData(prevData => ({
      ...prevData,
      isModalOpen: false,
    }));
  }, []);

  const contextValue: ProgressChecklistContextType = useMemo(() => ({
    state,
    markTaskCompleted,
    markTaskIncomplete,
    toggleChecklist,
    hideChecklist,
    showChecklist,
    dismissChecklist,
    restoreChecklist,
    resetProgress,
    openModal,
    closeModal,
    hideTask,
  }), [state, markTaskCompleted, markTaskIncomplete, toggleChecklist, hideChecklist, showChecklist, dismissChecklist, restoreChecklist, resetProgress, openModal, closeModal, hideTask]);

  return (
    <ProgressChecklistContext.Provider value={contextValue}>
      {children}
    </ProgressChecklistContext.Provider>
  );
}

export function useProgressChecklist() {
  const context = useContext(ProgressChecklistContext);
  if (context === undefined) {
    throw new Error('useProgressChecklist must be used within a ProgressChecklistProvider');
  }
  return context;
}
